<script setup>

import { KNOLIA } from "@/knolia";
import { onUpdated, defineProps, defineEmits } from "vue";
import { Gems } from "../gems";
import { useDragAndDrop } from "../store/modules/drag-and-drop";
import BaseSelect from "./BaseSelect.vue";

const props = defineProps({
    activities: {
        type: Array,
        default: () => []
    },
    currentJob: {
        type: Object,
        default: () => {}
    },
    draggable: {
        type: Boolean,
        default: false
    },
    userActivityControl: {
        type: Boolean,
        default: false
    }
})

const dragAndDrop = useDragAndDrop()

const emit = defineEmits(['remove-task', 'get-job-activities'])

async function SelectTaskAssignment(element, assignmentLevel) {
    if(element?.children)
        element.participation_level = assignmentLevel

    if(!props.draggable)
        KNOLIA.CallBackEnd('post', `set-operations&get=change-job-assignments&job_id=${props.currentJob.id}`, {
            element, is_activity: element.children != null
        }).then(() => {
            props.activities.forEach((activity, index) => {
                let removedTask = 0
                activity.children.forEach(task => {
                    removedTask += task.participation_level == 0 ? 1 : 0
                })
                if (removedTask == activity.children.length)
                    emit('remove-task', index)
            })
        })
}

function RemoveTaskFromJob(activityId) {
    KNOLIA.CallBackEnd('post', `set-operations&get=delete-task-of-job`, {
        activityId, jobId: props.currentJob.id
    })

    let indexToDelete

    props.activities.forEach((activity, index) => {
        if (activityId == activity.id)
            indexToDelete = index
    })

    emit('remove-task', indexToDelete)
}

function SetDragFunction(activity) {
    const activityElem = document.getElementById(`activity${activity.id}`)
    if(activityElem) {
        dragAndDrop.setDragFunctionElem(function () {
            const tasks = activity.children.map(task => {
                return {id: task.id, participation_level: task.participation_level}
            })

            dragAndDrop.setDraggedData({ activity, tasks })
        }, activityElem)
    }
}

onUpdated(() => {
    props.activities.forEach((elem, i) => {
        const removeIcon = document.getElementById(`remove-task-${i}`)

        if(removeIcon)
            Gems.Tooltip(removeIcon, 'Remover atividade da função', 'tooltip')

        SetDragFunction(elem)
    })
})

</script>

<template>
    <div class="div-activity-container" v-for="(activity, i) in activities" :key="activity.id" v-show="activity.showSelf == null ? true : activity.showSelf" :class="{'unavailable': activity.unavailable}">
        <div :draggable="draggable && !activity.unavailable" class="div-row bg-alt-line div-activity " :class="!draggable ? 'cursor-default' : 'cursor-pointer'" :id="draggable ? `activity${activity.id}` : ''">
            <div class="div-inner-activity-title">
                <i class="fas show-children cursor-pointer" :class="{'fa-chevron-down': !activity.showChildren , 'fa-chevron-up': activity.showChildren}" 
                    @click="activity.showChildren = !activity.showChildren"></i>
                <span class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">{{ activity.designation }}</span>
            </div>
            
            <div class="div-inner-activity-title" :class="{'div-activity-right-draggable': !draggable, 'div-activity-right': draggable && !userActivityControl, 'div-activity-right-control': userActivityControl}">
                <base-select class="cursor-pointer ddl-alt-padding" v-model="activity.participation_level" @change="SelectTaskAssignment(activity, $event.target.value)">
                    <option v-for="assignment in activity.participation_levels" :key="assignment.level" :value="assignment.level">
                        {{ assignment.designation }}
                    </option>
                </base-select>
                <i v-if="!draggable" @click="RemoveTaskFromJob(activity.id)" class="fa fa-times-circle remove-task cursor-pointer" :id="`remove-task-${i}`"></i>
            </div>

        </div>

        <div class="div-tasks" v-show="activity.showChildren">
            <div class="div-task-container div-row cursor-default"  :class="{'div-task-draggable': draggable && !userActivityControl}"
                v-for="task in activity.children" :key="task.id">
                <div class="div-task line-clamp line-clamp-1" @mouseover="Gems.TooltipIfNeeded($event.target)">{{ task.designation }}</div>

                <div class="div-inner-activity-title">
                    <base-select class="cursor-pointer ddl-alt-padding" v-model="task.participation_level" @change="SelectTaskAssignment(task, $event.target.value)">
                        <option :value="0"> [sem nível determinado] </option>
                        <option v-for="assignment in task.participation_levels" :key="assignment.level" :value="assignment.level">
                            {{ assignment.designation }}
                        </option>
                    </base-select>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

.div-activity-container:nth-child(n + 2) {
    margin-top: 2px;
}

.div-activity-container:nth-last-child(1) {
    margin-bottom: 0px !important;
}

.div-row {
    display: grid;
    grid-template-columns: 79% 20%;
    justify-content: space-between;
    gap: 10px;
    padding: 5px 15px;
    align-items: center;
    align-content: center;
}

.div-row:hover {
    background-color: var(--default-app-alt-line-color);
}

.div-job-row {
    cursor: default !important;
}

.div-activity:hover,
.div-activity:hover select {
    color: var(--default-app-hover-color);
    border-color: var(--default-app-hover-color);
}

.div-activity select,
.div-task-container select {
    background-color: var(--default-app-alt-line-color);
}

.div-activity,
.div-task-container {
    height: 35px;
    padding-left: 15px;
    padding-right: 10px;
}

.div-task-container {
    margin-top: 5px;
}

.div-task {
    padding-left: 29px
}

.remove-task {
    cursor: pointer;
    text-align: end;
}

.show-children {
    margin-right: 15px;
    pointer-events: all;
}

.unavailable {
    pointer-events: none;
    opacity: .3;
}

.job-task {
    margin-right: 29px;
}

.div-inner-activity-title {
    display: flex;
    align-items: center;
}

.div-activity-right {
    justify-content: end;
}

.div-activity-right-draggable {
    display: grid !important;
    grid-template-columns: 90% 10%;
}

.div-activity-right-control {
    display: grid !important;
    grid-template-columns: 100%;
}

.div-task-draggable {
    grid-template-columns: 79% 8%;
}

@media screen and (max-width: 765px) {
    .div-activity-container {
        font-size: .9em;
    }

    .div-activity {
        margin-bottom: 1px;
    }

    .div-task-container {
        margin-top: 1px;
    }

    .div-activity, .div-task-container {
        height:unset;
    }

    .div-row {
        padding: 2.5px 15px;
    }

    .div-row:not(:first-child)  {
        margin-top: 1px;
    }

    .div-row {
        grid-template-columns: 58% 37%;
    }
    
    .div-activity-right {
        display: grid;
        grid-template-columns: 100%;
    }
}
</style>